/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Lock, CheckCircle2, Loader2, Phone } from "lucide-react";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { getEffectiveRef } from "@/lib/refTracker";
import { markToolCompleted } from "@/lib/journeyTracker";
import { readJourneyRef } from "@/lib/journeyRef";
import { toast } from "sonner";

const QUESTIONS = [
  {
    key: "timore_principale",
    title: "Qual è il tuo principale timore in questo momento?",
    options: [
      { value: "prezzo", label: "Sbagliare il prezzo" },
      { value: "burocrazia", label: "Problemi burocratici" },
      { value: "energetica", label: "Svalutazione per la classe energetica" },
      { value: "non_so_iniziare", label: "Non so da dove iniziare" },
    ],
  },
  {
    key: "urgenza_vendita",
    title: "Entro quanto vorresti concludere la vendita?",
    options: [
      { value: "prima_possibile", label: "Prima possibile" },
      { value: "entro_6_mesi", label: "Entro 6 mesi" },
      { value: "non_ho_fretta", label: "Non ho fretta" },
      { value: "esplorando", label: "Sto solo esplorando" },
    ],
  },
  {
    key: "chiarezza_prezzo",
    title: "Hai già un'idea del prezzo a cui vorresti vendere?",
    options: [
      { value: "si", label: "Sì, ce l'ho" },
      { value: "no", label: "No, ho bisogno di capirlo" },
      { value: "ragionando", label: "Ci sto ragionando" },
    ],
  },
  {
    key: "classe_energetica",
    title: "Conosci la classe energetica del tuo immobile?",
    options: [
      { value: "a_b", label: "Sì, è in classe A o B" },
      { value: "c_d", label: "Sì, è in classe C o D" },
      { value: "e_f_g", label: "Sì, è in classe E, F o G" },
      { value: "non_so", label: "No, non lo so" },
    ],
  },
  {
    key: "q_ape",
    title: "Hai l'Attestato di Prestazione Energetica (APE) aggiornato?",
    options: [
      { value: "si", label: "Sì" },
      { value: "no", label: "No" },
      { value: "non_so", label: "Non so" },
    ],
  },
  {
    key: "q_planimetria",
    title: "La planimetria catastale è conforme alla realtà dell'immobile?",
    options: [
      { value: "si", label: "Sì" },
      { value: "no", label: "No" },
      { value: "non_so", label: "Non so" },
    ],
  },
  {
    key: "q_difformita",
    title: "Ci sono difformità edilizie o abusi da sanare?",
    options: [
      { value: "no_regola", label: "No, tutto in regola" },
      { value: "si_conoscenza", label: "Sì, ne sono a conoscenza" },
      { value: "non_verificato", label: "Non ho verificato" },
    ],
  },
  {
    key: "q_mutuo",
    title: "Hai un mutuo residuo sull'immobile?",
    options: [
      { value: "si", label: "Sì" },
      { value: "no", label: "No" },
    ],
  },
  {
    key: "q_accordo_proprieta",
    title: "Tutti i comproprietari sono d'accordo a vendere?",
    options: [
      { value: "solo_io", label: "Sono l'unico proprietario" },
      { value: "tutti_accordo", label: "Sì, siamo tutti d'accordo" },
      { value: "opinioni_diverse", label: "Ci sono opinioni diverse" },
    ],
  },
  {
    key: "q_agente",
    title: "Hai già parlato con un agente immobiliare negli ultimi 6 mesi?",
    options: [
      { value: "si", label: "Sì" },
      { value: "no", label: "No" },
    ],
  },
];

const STORAGE_KEY = "partenza_funnel_state";
const IDEMPOTENCY_STORAGE_KEY = "partenza_funnel_idempotency";
// Total user-facing screens: 1 intro + 10 questions + 1 contacts = 12
const TOTAL_SCREENS = 12;

function getOrCreateIdempotencyKey() {
  try {
    const existing = sessionStorage.getItem(IDEMPOTENCY_STORAGE_KEY);
    if (existing) return existing;
    const created = `partenza-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(IDEMPOTENCY_STORAGE_KEY, created);
    return created;
  } catch {
    return `partenza-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default function PartenzaPage() {
  const [params] = useSearchParams();
  const refFromUrl = params.get("ref") || "";
  const emailFromUrl = params.get("email") || "";

  const [screen, setScreen] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { id, punteggio, semaforo, whatsapp_url }
  const [journeyMode, setJourneyMode] = useState(false);
  const [idempotencyKey] = useState(getOrCreateIdempotencyKey);
  // Wait for /orizzonti/verify to complete before enabling the final step,
  // but ONLY if a NON-EXPIRED vg_journey_ref exists (TTL enforced client-side
  // so a stale journey from an unrelated past analysis never re-hydrates
  // identity into a new PartenzaPro session).
  const [identityLoading, setIdentityLoading] = useState(() => !!readJourneyRef());
  const [journeyRef, setJourneyRef] = useState({ j: null, t: null });

  const [data, setData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) return JSON.parse(raw);
    } catch (e) {
      // ignore storage read errors
    }
    const empty = { nome: "", email: emailFromUrl || "", telefono: "", ref: refFromUrl || getEffectiveRef() || "", journey_id: null };
    QUESTIONS.forEach((q) => { empty[q.key] = null; });
    return empty;
  });

  useEffect(() => { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data)); }, [data]);
  useEffect(() => {
    const effective = refFromUrl || getEffectiveRef() || "";
    if (effective) setData((d) => ({ ...d, ref: effective }));
  }, [refFromUrl]);

  // Journey continuity: read vg_journey_ref once, verify via existing endpoint,
  // and merge nome/email/referral/journey_id into state WITHOUT overwriting
  // values already present. Same mechanism used by ValutaPro/Funnel.jsx.
  useEffect(() => {
    let cancelled = false;
    async function hydrate() {
      const ref = readJourneyRef();
      if (!ref) return;
      try {
        const r = await api.get("/orizzonti/verify", { params: { j: ref.j, t: ref.t } });
        if (cancelled || !r?.data?.ok) return;
        setJourneyMode(true);
        setJourneyRef({ j: ref.j, t: ref.t });
        setData((d) => ({
          ...d,
          nome: d.nome || (r.data.nome || ""),
          email: d.email || (r.data.email || ""),
          ref: d.ref || (r.data.referral_code || ""),
          journey_id: d.journey_id || (r.data.journey_id || null),
        }));
      } catch {
        // Verifica fallita o token scaduto: fallback autonomo
      } finally {
        if (!cancelled) setIdentityLoading(false);
      }
    }
    hydrate();
    return () => { cancelled = true; };
  }, []);

  const upd = (patch) => setData((d) => ({ ...d, ...patch }));

  const currentQuestion = screen >= 2 && screen <= 11 ? QUESTIONS[screen - 2] : null;
  const canAdvanceQuestion = currentQuestion ? !!data[currentQuestion.key] : false;
  const nomeOk = data.nome.trim().length >= 2;
  const emailOk = /^\S+@\S+\.\S+$/.test((data.email || "").trim());
  const hasIdentity = nomeOk && emailOk;
  const missingOnlyNome = !nomeOk && emailOk;
  const missingOnlyEmail = nomeOk && !emailOk;
  const identityValid = nomeOk && emailOk;
  const canSubmit = identityValid && !identityLoading;
  // Se l'identità è nota, il flusso finisce alla domanda 10 (schermo 11) e la
  // CTA della domanda 10 diventa "Invia e ricevi la diagnosi": totale 11/11.
  // Se manca un dato: totale 12/12 e schermo 12 richiede solo il mancante.
  // Ingresso diretto: totale 12/12 con form completo (comportamento attuale).
  const TOTAL_SCREENS_EFFECTIVE = hasIdentity ? 11 : 12;

  const goNext = () => setScreen((s) => Math.min(TOTAL_SCREENS_EFFECTIVE, s + 1));
  const goBack = () => setScreen((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const payload = {
        nome: data.nome.trim(),
        email: data.email.trim(),
        telefono: (data.telefono || "").trim() || "",
        referral_code_origine: data.ref || null,
        idempotency_key: idempotencyKey,
      };
      QUESTIONS.forEach((q) => { payload[q.key] = data[q.key]; });
      const r = await api.post("/partenza/submit", payload);
      setResult(r.data);
      setSubmitted(true);
      sessionStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(IDEMPOTENCY_STORAGE_KEY);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return <SuccessScreen nome={data.nome} email={data.email} answers={data} result={result} />;
  }

  return (
    <div className="vg-funnel-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 pb-12">
        <div className="flex items-center justify-between mb-6">
          {screen === 1 ? (
            <a
              href="/app"
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="partenza-back-btn"
            >
              <ArrowLeft size={16} /> Esci
            </a>
          ) : (
            <button
              onClick={goBack}
              className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
              data-testid="partenza-back-btn"
            >
              <ArrowLeft size={16} /> Indietro
            </button>
          )}
          <Logo size={22} />
          <div className="text-xs text-gray-500" data-testid="partenza-step-indicator">
            {screen === 1
              ? "Inizio"
              : (!hasIdentity && screen === TOTAL_SCREENS_EFFECTIVE)
                ? "Quasi fatto"
                : `Domanda ${Math.min(screen - 1, 10)} di 10`}
          </div>
        </div>

        <div className="vg-progress mb-10">
          <div className="vg-progress-fill" style={{ width: `${(screen / TOTAL_SCREENS_EFFECTIVE) * 100}%` }} />
        </div>

        <div className="fade-slide" key={screen}>
          {screen === 1 && <IntroScreen onStart={goNext} />}
          {currentQuestion && (
            <QuestionScreen
              q={currentQuestion}
              value={data[currentQuestion.key]}
              onChange={(v) => upd({ [currentQuestion.key]: v })}
              canNext={canAdvanceQuestion}
              onNext={goNext}
              onSubmitFinal={submit}
              submittingFinal={submitting}
              isFinalWithIdentity={hasIdentity && screen === 11}
              index={screen - 1}
              isFirst={screen === 2}
            />
          )}
          {!hasIdentity && screen === 12 && (
            <ContactsScreen
              data={data}
              upd={upd}
              canSubmit={canSubmit}
              submit={submit}
              submitting={submitting}
              journeyMode={journeyMode}
              identityLoading={identityLoading}
              missingOnlyNome={missingOnlyNome}
              missingOnlyEmail={missingOnlyEmail}
            />
          )}
        </div>

        <div className="mt-12 text-center">
          <a
            href="/scopri"
            className="text-lg font-medium hover:underline"
            style={{ color: "#B40101" }}
            data-testid="partenza-howitworks-link"
          >
            Come funziona PartenzaPro
          </a>
        </div>
      </div>
    </div>
  );
}

// ===================== SCREENS =====================
function IntroScreen({ onStart }) {
  return (
    <div className="vg-card p-6 sm:p-10 text-center">
      <h1 className="text-3xl sm:text-5xl font-bold text-[#0A0A0A] leading-tight" data-testid="partenza-intro-title">
        Sei davvero pronto a vendere?
      </h1>
      <p className="text-base sm:text-lg text-gray-500 mt-5 max-w-xl mx-auto leading-relaxed">
        10 domande per capire dove sei, cosa manca e qual è il passo giusto da fare adesso.
      </p>

      <div
        className="mt-10 mx-auto max-w-xl rounded-[12px] px-6 py-7 text-center"
        style={{ background: "#F5F5F5" }}
        data-testid="partenza-mirror-block"
      >
        <p
          className="text-[#0A0A0A] font-semibold"
          style={{ fontSize: "17px", lineHeight: 1.65 }}
          data-testid="partenza-mirror-line-1"
        >
          Stai pensando di vendere ma non sai da dove iniziare.
        </p>
        <p
          className="text-[#0A0A0A] mt-3 leading-relaxed"
          style={{ fontSize: "17px", lineHeight: 1.65 }}
          data-testid="partenza-mirror-line-2"
        >
          Vendere casa solleva sempre una domanda prima di tutte le altre. Scopriamo insieme qual è la tua.
        </p>
      </div>

      <div className="mt-8 flex justify-center">
        <button
          onClick={onStart}
          className="vg-btn-primary"
          style={{ background: "#B40101" }}
          data-testid="partenza-start-btn"
        >
          Inizia la diagnosi gratuita <ArrowRight size={18} />
        </button>
      </div>

      <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-500">
        <Lock size={14} />
        <span>Diagnosi gratuita. Nessuno spam. I tuoi dati restano protetti.</span>
      </div>
    </div>
  );
}

function QuestionScreen({ q, value, onChange, canNext, onNext, onSubmitFinal, submittingFinal, isFinalWithIdentity, index, isFirst = false }) {
  const revealClass = isFirst ? "vg-reveal-first" : "";
  const childClass = isFirst ? "vg-reveal-child" : "";
  const ctaClass = isFirst ? "vg-reveal-cta" : "";
  return (
    <div className={`vg-card p-6 sm:p-10 ${revealClass}`} data-testid={isFirst ? "partenza-q1-reveal" : undefined}>
      <h1 className={`text-2xl sm:text-3xl font-bold text-[#0A0A0A] leading-tight ${childClass}`} data-testid={`partenza-q-title-${q.key}`}>
        {q.title}
      </h1>

      <div className={`space-y-3 mt-8 ${childClass}`}>
        {q.options.map((o) => {
          const sel = value === o.value;
          return (
            <button
              key={o.value}
              onClick={() => onChange(o.value)}
              className={`vg-pill-option ${sel ? "selected" : ""}`}
              data-testid={`partenza-opt-${q.key}-${o.value}`}
            >
              <span className={`vg-checkbox ${sel ? "checked" : ""}`} style={{ borderRadius: 999 }}>
                {sel && <span style={{ width: 10, height: 10, background: "#fff", borderRadius: 999 }} />}
              </span>
              <span className="font-semibold text-left">{o.label}</span>
            </button>
          );
        })}
      </div>

      <div className={`mt-10 flex justify-end ${ctaClass}`}>
        {isFinalWithIdentity ? (
          <button
            className="vg-btn-primary"
            disabled={!canNext || submittingFinal}
            onClick={onSubmitFinal}
            data-testid={`partenza-submit-final-${q.key}`}
          >
            {submittingFinal
              ? (<><Loader2 className="animate-spin" size={18} /> Invio in corso…</>)
              : (<>Invia e ricevi la diagnosi <ArrowRight size={18} /></>)}
          </button>
        ) : (
          <button
            className="vg-btn-primary"
            disabled={!canNext}
            onClick={onNext}
            data-testid={`partenza-next-${q.key}`}
          >
            Continua <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}

function ContactsScreen({ data, upd, canSubmit, submit, submitting, journeyMode, identityLoading, missingOnlyNome, missingOnlyEmail }) {
  if (identityLoading) {
    return (
      <div className="vg-card p-6 sm:p-10 text-center" data-testid="partenza-contacts-loading">
        <Loader2 className="animate-spin mx-auto text-[#B40101]" size={26} />
        <p className="mt-4 text-[#666]">Recupero dell'identità dal tuo percorso in corso…</p>
      </div>
    );
  }
  // Se manca soltanto un campo (nome o email), chiedilo senza mostrare l'altro.
  if (missingOnlyNome || missingOnlyEmail) {
    const askNome = missingOnlyNome;
    return (
      <div className="vg-card p-6 sm:p-10" data-testid={`partenza-contacts-only-${askNome ? "nome" : "email"}`}>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">
          Manca solo {askNome ? "il tuo nome" : "la tua email"}
        </h1>
        <p className="text-base text-gray-500 mt-2">
          {askNome
            ? "Ci serve un nome da usare nella diagnosi che riceverai via email."
            : "Ci serve un indirizzo email a cui inviare la diagnosi completa."}
        </p>
        <div className="mt-8">
          {askNome ? (
            <Field label="Nome">
              <input
                className="vg-input"
                value={data.nome}
                onChange={(e) => upd({ nome: e.target.value })}
                data-testid="partenza-nome-input"
                placeholder="Il tuo nome"
                autoFocus
              />
            </Field>
          ) : (
            <Field label="Email">
              <input
                type="email"
                className="vg-input"
                value={data.email}
                onChange={(e) => upd({ email: e.target.value })}
                data-testid="partenza-email-input"
                placeholder="latuaemail@dominio.it"
                autoFocus
              />
            </Field>
          )}
        </div>
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Lock size={14} className="mt-0.5 flex-shrink-0" />
            <span>Dati protetti. Non condividiamo con terzi.</span>
          </div>
          <button
            className="vg-btn-primary"
            disabled={!canSubmit || submitting}
            onClick={submit}
            data-testid="partenza-submit"
          >
            {submitting ? (
              <><Loader2 className="animate-spin" size={18} /> Invio in corso…</>
            ) : (
              <>Invia e ricevi la diagnosi <ArrowRight size={18} /></>
            )}
          </button>
        </div>
      </div>
    );
  }
  if (journeyMode) {
    return (
      <div className="vg-card p-6 sm:p-10" data-testid="partenza-contacts-journey">
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">La tua diagnosi PartenzaPro è pronta da inviare</h1>
        <p className="text-base text-gray-500 mt-2">
          Continuiamo dal tuo percorso: useremo l'identità già associata alla tua analisi OrizzontiPro.
        </p>
        <div className="mt-6 rounded-[12px] bg-[#FAF7F2] border border-[#EDE4D3] p-5 text-sm text-[#0A0A0A] leading-relaxed">
          <div><span className="text-[#6B6B6B]">Verrà inviata a</span> <strong className="text-[#0A0A0A]">{data.nome || "—"}</strong></div>
          <div className="mt-1"><span className="text-[#6B6B6B]">Email</span> <strong className="text-[#0A0A0A]">{data.email || "—"}</strong></div>
        </div>
        <div className="mt-4 p-4 rounded-[12px] bg-gray-50 border-l-4 border-[#B40101] text-sm text-[#0A0A0A] leading-relaxed" data-testid="partenza-contacts-info">
          Riceverai la diagnosi completa via email. Al termine potrai scegliere se chiedere il contatto con un Property Advisor.
        </div>
        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-start gap-2 text-xs text-gray-500">
            <Lock size={14} className="mt-0.5 flex-shrink-0" />
            <span>Dati protetti. Non condividiamo con terzi.</span>
          </div>
          <button
            className="vg-btn-primary"
            disabled={!canSubmit || submitting}
            onClick={submit}
            data-testid="partenza-submit"
          >
            {submitting ? (
              <><Loader2 className="animate-spin" size={18} /> Invio in corso…</>
            ) : (
              <>Invia e ricevi la diagnosi <ArrowRight size={18} /></>
            )}
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className="vg-card p-6 sm:p-10" data-testid="partenza-contacts-autonomous">
      <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A]">Dove ti mandiamo la tua diagnosi completa?</h1>
      <p className="text-base text-gray-500 mt-2">Riceverai una diagnosi personalizzata della tua situazione via email.</p>

      <div className="space-y-5 mt-8">
        <Field label="Nome">
          <input
            className="vg-input"
            value={data.nome}
            onChange={(e) => upd({ nome: e.target.value })}
            data-testid="partenza-nome-input"
            placeholder="Il tuo nome"
          />
        </Field>
        <Field label="Email">
          <input
            className="vg-input"
            type="email"
            value={data.email}
            onChange={(e) => upd({ email: e.target.value })}
            data-testid="partenza-email-input"
            placeholder="tua@email.it"
          />
        </Field>
      </div>

      <div className="mt-6 p-4 rounded-[12px] bg-gray-50 border-l-4 border-[#B40101] text-sm text-[#0A0A0A] leading-relaxed" data-testid="partenza-contacts-info">
        Riceverai la diagnosi completa via email. Al termine potrai scegliere se chiedere il contatto con un Property Advisor.
      </div>

      <div className="mt-8 flex justify-end">
        <button
          className="vg-btn-primary"
          disabled={!canSubmit || submitting}
          onClick={submit}
          data-testid="partenza-submit"
        >
          {submitting ? (
            <><Loader2 className="animate-spin" size={18} /> Invio in corso…</>
          ) : (
            <>Invia e ricevi la diagnosi <ArrowRight size={18} /></>
          )}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#0A0A0A] mb-2">{label}</label>
      {children}
    </div>
  );
}

const TIMORE_LABELS = {
  prezzo: "Sbagliare il prezzo di uscita",
  burocrazia: "Nodi burocratici e documentali",
  energetica: "Impatto della classe energetica",
  non_so_iniziare: "Non sapere da dove iniziare",
};

const SEMAFORO_CFG = {
  verde: { chip: "#10B981", chipBg: "#ECFDF5", chipText: "#065F46" },
  giallo: { chip: "#F59E0B", chipBg: "#FFF7ED", chipText: "#B45309" },
  rosso: { chip: "#B40101", chipBg: "#FEF2F2", chipText: "#B40101" },
};

// Deterministic B2C branding of the raw score. Mirror of the backend logic in
// partenza_service.compute_public_score. Kept identical so the frontend can
// still render a coherent label if the API omits `public_score`.
function computePublicScoreClient(raw) {
  const r = Number.isFinite(raw) ? raw : 0;
  const p = Math.min(90, Math.max(20, r));
  let label;
  if (p < 40) label = "Molto da preparare";
  else if (p < 60) label = "Preparazione parziale";
  else if (p < 75) label = "Buon livello di preparazione";
  else label = "Preparazione avanzata";
  return { public: p, label };
}

function SuccessScreen({ nome, email, answers, result }) {
  const [resendBusy, setResendBusy] = React.useState(false);
  const fallback = `https://wa.me/393925822212?text=${encodeURIComponent("Ciao, ho appena completato la diagnosi PartenzaPro e vorrei parlare con un Property Advisor.")}`;
  const whatsappLink = result?.whatsapp_url || fallback;
  const semaforo = result?.semaforo || "rosso";
  const cfg = SEMAFORO_CFG[semaforo] || SEMAFORO_CFG.rosso;
  const ostacolo = TIMORE_LABELS[answers?.timore_principale] || "Ancora poca chiarezza su come procedere";
  const publicFromApi = typeof result?.public_score === "number" ? { public: result.public_score, label: result.public_label } : null;
  const publicComputed = publicFromApi || computePublicScoreClient(result?.punteggio || 0);
  const publicScore = publicComputed.public;
  const publicLabel = publicComputed.label || "";

  React.useEffect(() => {
    if (email) markToolCompleted("partenza", { email, nome });
  }, [email, nome]);

  const handleResend = async () => {
    if (resendBusy || !email) return;
    setResendBusy(true);
    try {
      await api.post("/email/resend", { tool: "partenza", email });
      toast.success("Email rispedita. Controlla la tua casella.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile rispedire ora. Riprova tra qualche minuto.");
    } finally {
      setResendBusy(false);
    }
  };

  return (
    <div className="vg-funnel-bg">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-16">
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: "#B40101" }}>
            <CheckCircle2 size={40} color="#fff" />
          </div>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] text-center" data-testid="partenza-success-title">
          {nome ? `Ottimo, ${nome}!` : "Ottimo lavoro!"}
        </h1>
        <p className="text-base text-[#666] max-w-xl mx-auto mt-3 leading-relaxed text-center">
          Ti abbiamo inviato la diagnosi PartenzaPro completa via email
          {email ? <> (<strong className="text-[#0A0A0A]">{email}</strong>)</> : null}.
        </p>

        <div className="vg-card p-6 sm:p-8 mt-8 max-w-2xl mx-auto">
          <div className="flex items-center gap-3 flex-wrap">
            <span
              className="text-xs font-bold uppercase tracking-widest px-3 py-1 rounded-full"
              style={{ background: cfg.chipBg, color: cfg.chipText }}
              data-testid="pp-insight-band"
            >
              <span className="inline-block w-2 h-2 rounded-full align-middle mr-2" style={{ background: cfg.chip }} />
              {publicLabel}
            </span>
            <span className="text-sm text-[#6B6B6B] ml-auto" data-testid="pp-insight-score">
              Livello indicativo: <strong className="text-[#0A0A0A]">{publicScore}/90</strong>
            </span>
          </div>
          <div className="mt-5 rounded-[12px] bg-[#FAF7F2] border border-[#EDE4D3] p-4">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#B40101] mb-1">
              Ostacolo principale emerso
            </div>
            <p className="text-sm text-[#0A0A0A] leading-relaxed">{ostacolo}</p>
          </div>

          <div className="mt-5 p-4 rounded-[12px] bg-gray-50 border-l-4 border-[#B40101] text-sm text-[#0A0A0A] leading-relaxed" data-testid="pp-email-notice">
            Le criticità nel dettaglio, i punti forti da valorizzare e le azioni consigliate sono nell'email. Se non arriva entro pochi minuti, controlla lo spam oppure {" "}
            <button
              className="underline font-semibold disabled:opacity-60"
              onClick={handleResend}
              disabled={resendBusy || !email}
              data-testid="pp-resend-btn"
            >
              {resendBusy ? "rispedizione in corso…" : "rispedisci l'email"}
            </button>.
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B40101] hover:bg-[#8E0101] text-white font-semibold px-5 py-3 rounded-xl transition-colors"
              data-testid="pp-cta-talk-pa"
            >
              <Phone size={16} /> Voglio parlare con il mio Property Advisor
            </a>
            <a
              href="/app"
              className="flex-1 inline-flex items-center justify-center gap-2 border border-[#B40101]/40 text-[#B40101] font-semibold px-5 py-3 rounded-xl hover:bg-[#B40101]/5 transition-colors"
              data-testid="pp-cta-back-to-journey"
            >
              Torna al percorso <ArrowRight size={18} />
            </a>
          </div>
        </div>

        <p className="text-xs text-gray-500 mt-6 text-center max-w-2xl mx-auto leading-relaxed">
          Hai completato il percorso e messo a fuoco obiettivo, valore e preparazione. Il Property Advisor può ora trasformare il quadro in tre indicazioni concrete: priorità, rischi e prossimo passo. Il contatto avviene soltanto se lo richiedi.
        </p>
      </div>
    </div>
  );
}
